import type { QueryClient } from "@tanstack/react-query";
import type { AnyRouter, useNavigate } from "@tanstack/react-router";
import { getImageByIdApiImagesImageIdGetQueryKey } from "../generated/api/@tanstack/react-query.gen";
import type { HydratedImage, NormalizedBox } from "../generated/api/types.gen";
import { trackEvent } from "./analytics";
import { api, getErrorMessage } from "./api-client";
import type { ComponentReplacementSelection } from "./component-replacement";
import { toAsyncError } from "./errors";
import { invalidateGallerySessions, invalidateIterationUsage } from "./queries";

/** What every selection action needs to open its result. */
interface SelectionActionDeps {
  queryClient: QueryClient;
  router: AnyRouter;
  navigate: ReturnType<typeof useNavigate>;
  /** Called once the result is open. Navigating /refine -> /refine only
   *  swaps search params, so the caller must tear its own state down. */
  onSuccess: () => void;
  onFailure: (message: string) => void;
}

export interface RunCreateDesignParams extends SelectionActionDeps {
  sourceImageId: string;
  crop: NormalizedBox;
}

export interface RunReplaceFromSelectionParams extends SelectionActionDeps {
  sourceImageId: string;
  crop: NormalizedBox;
  /** The picked asset: the component to swap in. */
  selection: ComponentReplacementSelection;
}

/**
 * Consume a selection stream, seed the image cache and open the result in
 * Refine.
 *
 * The server identifies what the region contains before generating; an
 * unrecognized selection arrives as a stream error and lands in `onFailure`.
 * Navigates as soon as the image arrives, matching `handleRegenerationSuccess`,
 * so the user lands on the result while the gallery refreshes behind them.
 */
async function openSelectionStreamResult({
  stream,
  emptyMessage,
  queryClient,
  router,
  navigate,
  onSuccess,
  onFailure,
  flow,
}: SelectionActionDeps & {
  stream: AsyncIterable<HydratedImage>;
  emptyMessage: string;
  flow: string;
}): Promise<void> {
  let image: HydratedImage | null = null;

  try {
    for await (const event of stream) {
      image = event;
    }

    if (!image) {
      onFailure(emptyMessage);
      return;
    }

    // Seed the cache so Refine renders immediately instead of refetching.
    queryClient.setQueryData(
      getImageByIdApiImagesImageIdGetQueryKey({
        path: { image_id: image.id },
      }),
      image,
    );
    navigate({
      to: "/refine",
      search: { imageId: image.id, sessionId: image.session_id },
    });
    onSuccess();

    await invalidateGallerySessions(queryClient, router);
    // A selection spends an iteration like any other generation; re-read the
    // allowance so the overlay on /refine reflects the spend immediately.
    await invalidateIterationUsage(queryClient);
  } catch (err) {
    // A 403 here is almost always a spent allowance the cache hadn't caught
    // up with; re-read it so the overlay appears rather than a bare failure.
    if (toAsyncError(err)?.status === 403) {
      await invalidateIterationUsage(queryClient);
      trackEvent("iteration_limit_hit", { flow });
    }
    onFailure(getErrorMessage(err));
  }
}

/**
 * Turn the selection into a new design and open it in Refine.
 */
export async function runCreateDesignFromSelection({
  sourceImageId,
  crop,
  ...deps
}: RunCreateDesignParams): Promise<void> {
  await openSelectionStreamResult({
    ...deps,
    stream: api.selection.createDesignStream(sourceImageId, { crop }),
    emptyMessage: "The new design could not be created. Please try again.",
    flow: "selection_create",
  });
}

/**
 * Replace the selected component with the picked asset and open the result.
 *
 * The result is a child of the source image in the same session, so this
 * navigates like a regeneration rather than opening a new design. The seeded
 * parameter_groups already carry the stamped replacement.
 */
export async function runReplaceFromSelection({
  sourceImageId,
  crop,
  selection,
  ...deps
}: RunReplaceFromSelectionParams): Promise<void> {
  await openSelectionStreamResult({
    ...deps,
    stream: api.selection.replaceAssetStream(sourceImageId, {
      crop,
      url: selection.url,
      name: selection.name,
      asset_id: selection.assetId ?? null,
    }),
    emptyMessage: "The replacement could not be applied. Please try again.",
    flow: "selection_replace",
  });
}
